import { useState } from "react"
import { useQuery } from "urql"
import posthog from "posthog-js"
import { Moon, Sun, Monitor, Settings, Circle, PaintbrushVertical, Building2, LogOut, User } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useTheme } from "@/components/theme-provider"
import { useAuth } from "@/hooks/use-auth"
import { useServerInfo } from "@/hooks/use-server-info"
import { useSession } from "@/hooks/use-session"
import { useWsStatus } from "@/hooks/use-ws-status"
import { OrganizationsQuery } from "@/lib/graphql/queries"

async function handleLogout() {
  try {
    await fetch("/api/v1/saas/auth/logout", { method: "POST", credentials: "include" })
  } catch {
    // Session cookie is HttpOnly, server already cleared it on its side
    // for any in-progress logout. We force-navigate regardless.
  }
  posthog.reset()
  window.location.assign("/login")
}

export function Topbar() {
  const { setTheme } = useTheme()
  const { apiKey, setApiKey, clearApiKey, isConfigured } = useAuth()
  const { info } = useServerInfo()
  const session = useSession()
  const wsStatus = useWsStatus()
  const [keyInput, setKeyInput] = useState(apiKey)
  const isSaas = info?.edition === "saas"
  const sessionUser = session.status === "authenticated" ? session.user : null

  const [orgsResult] = useQuery({
    query: OrganizationsQuery,
    variables: { limit: 10 },
    pause: !isConfigured,
  })
  const orgs = orgsResult.data?.organizations.nodes ?? []
  const currentOrg = orgs[0]

  return (
    <header className="flex items-center justify-end h-14 px-6 border-b border-border/50 bg-sidebar">
      <div className="flex items-center gap-2">
        {currentOrg && (
          orgs.length > 1 ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  {currentOrg.name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="text-xs">Organizations</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {orgs.map((org) => (
                  <DropdownMenuItem key={org.id} className="text-xs">
                    <Building2 className="h-3.5 w-3.5 mr-2" />
                    {org.name}
                    {org.id === currentOrg.id && (
                      <span className="ml-auto text-2xs text-muted-foreground">Current</span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-1.5 px-2 text-xs text-muted-foreground">
              <Building2 className="h-3.5 w-3.5" />
              {currentOrg.name}
            </div>
          )
        )}

        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 px-2">
              <Circle
                className={cn(
                  "h-2 w-2",
                  wsStatus === "connected" && "fill-emerald-500 text-emerald-500 animate-pulse-dot",
                  wsStatus === "connecting" && "fill-amber-500 text-amber-500 animate-pulse-dot",
                  wsStatus === "disconnected" && "fill-muted-foreground/50 text-muted-foreground/50",
                )}
              />
              <span className="text-xs text-muted-foreground capitalize">{wsStatus}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {wsStatus === "connected" && "WebSocket connected"}
            {wsStatus === "connecting" && "WebSocket connecting..."}
            {wsStatus === "disconnected" && "WebSocket disconnected, subscriptions will reconnect automatically"}
          </TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 transition-colors" aria-label="Toggle theme">
              <PaintbrushVertical className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => setTheme("light", { x: e.clientX, y: e.clientY })}>
              <Sun className="h-4 w-4 mr-2" /> Light
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => setTheme("dark", { x: e.clientX, y: e.clientY })}>
              <Moon className="h-4 w-4 mr-2" /> Dark
            </DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => setTheme("system", { x: e.clientX, y: e.clientY })}>
              <Monitor className="h-4 w-4 mr-2" /> System
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {!isSaas && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 transition-colors" aria-label="Settings">
              <Settings className="size-5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80" onOpenAutoFocus={(e) => e.preventDefault()}>
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium">API Key</h4>
                <p className="text-xs text-muted-foreground">
                  Required when server has auth enabled
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="api-key" className="text-xs">Key</Label>
                <Input
                  id="api-key"
                  type="password"
                  placeholder="Enter API key..."
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  className="font-mono text-xs"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={() => setApiKey(keyInput)}
                >
                  Save
                </Button>
                {isConfigured && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      clearApiKey()
                      setKeyInput("")
                    }}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
        )}

        {isSaas && sessionUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 transition-colors" aria-label="Account">
                <User className="size-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
                <span className="text-xs font-medium text-foreground">{sessionUser.name}</span>
                <span className="text-2xs text-muted-foreground truncate">{sessionUser.email}</span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-xs">
                <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  )
}
